import { api } from '../apiClient';

export interface TransferablePass {
  id: string;
  identifier: string;
  name: string;
  email: string;
  phone: string | null;
  event: {
    id: string | null;
    title: string | null;
    slug: string | null;
    banner_url: string | null;
  };
  category: {
    id: string | null;
    name: string | null;
    seated: boolean;
  };
  seat_info: {
    number: string;
    row: string;
    column: string;
  } | null;
  expires: string | null;
  transfer_fee: number;
  transfer_fee_type: 'percentage' | 'fixed' | null;
  currency_symbol: string | null;
}

export interface VerifyPassResponse {
  success: boolean;
  verified: boolean;
  pass?: {
    id: string;
    identifier: string;
    name: string;
    email: string;
    phone: string | null;
    event: {
      title: string | null;
      slug: string | null;
    };
    category: {
      name: string | null;
    };
    additional_fields: Record<string, string>;
    photo_url: string | null;
    seat_info: {
      number: string;
      row: string;
      column: string;
    } | null;
  };
  error?: string;
}

export interface PassOTPResponse {
  success: boolean;
  message: string;
  passes_count?: number;
}

export interface PassOTPVerifyResponse {
  success: boolean;
  message: string;
  passes?: Array<{
    id: string;
    identifier: string;
    event_title: string;
    category_name: string;
    event_banner_url?: string | null;
    event_thumbnail_url?: string | null;
    created_time: string;
  }>;
}

export interface DownloadTokenResponse {
  success: boolean;
  download_token?: string;
  message?: string;
}

export const passService = {
  download: async (passId: string, action?: 'google-wallet'): Promise<{ success: boolean; redirect_url?: string; error?: string }> => {
    const params = action ? { action } : {};
    const response = await api.get(`/passes/${passId}/download/`, { params });
    return response.data;
  },

  // Transfer tickets
  getTransferablePasses: async (): Promise<{ passes: TransferablePass[] }> => {
    const response = await api.get('/passes/transfer/list/');
    return response.data;
  },

  createTransferRequest: async (data: {
    pass_id: string;
    new_name: string;
    new_email: string;
    new_phone?: string;
  }): Promise<{ success: boolean; message: string; cart_id: number; transfer_fee: number }> => {
    const response = await api.post('/passes/transfer/create/', data);
    return response.data;
  },

  // Verify pass
  verifyPass: async (identifier: string, email: string): Promise<VerifyPassResponse> => {
    const response = await api.post('/passes/verify/', { identifier, email });
    return response.data;
  },

  // Secure pass access (OTP)
  sendPassOTP: async (contactMethod: 'email' | 'phone', contactValue: string): Promise<PassOTPResponse> => {
    const response = await api.post('/passes/otp/send/', {
      contact_method: contactMethod,
      contact_value: contactValue,
    });
    return response.data;
  },

  verifyPassOTP: async (contactValue: string, otp: string): Promise<PassOTPVerifyResponse> => {
    const response = await api.post('/passes/otp/verify/', {
      contact_value: contactValue,
      otp,
    });
    return response.data;
  },

  generateDownloadToken: async (passId: string, contactValue: string): Promise<DownloadTokenResponse> => {
    const response = await api.post(`/passes/${passId}/otp/token/`, {
      contact_value: contactValue,
    });
    return response.data;
  },

  downloadWithOTP: async (passId: string, token: string, action?: 'google-wallet', format?: 'pdf' | 'image'): Promise<void> => {
    const params: any = { token };
    if (action) params.action = action;
    if (format === 'pdf') {
      params.format = 'pdf';
    }
    // For image, don't set format param (default behavior)
    
    // Use API endpoint - api client has baseURL configured
    const url = `/passes/${passId}/otp/download/`;
    
    if (action === 'google-wallet') {
      // For Google Wallet, redirect to the URL
      window.location.href = api.defaults.baseURL + url + '?' + new URLSearchParams(params).toString();
      return;
    }
    
    try {
      // Use api client with blob response type
      const response = await api.get(url, {
        params: params,
        responseType: 'blob', // Important: tell axios to handle as blob
      });
      
      // Get the blob from response
      const blob = response.data as Blob;
      
      // Check content type from response headers
      const contentType = response.headers['content-type'] || '';
      const isPDF = contentType.includes('application/pdf');
      const isImage = contentType.includes('image/');
      
      // If response status indicates error, try to parse error message
      if (response.status >= 400) {
        try {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || `Server error: ${response.status}`);
        } catch (e) {
          throw new Error(`Server error: ${response.status}`);
        }
      }
      
      // Verify it's actually a file
      if (!isPDF && !isImage) {
        // Try to read as text to get error message
        try {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || 'Invalid file type received');
        } catch (e) {
          throw new Error('Invalid file type received');
        }
      }
      
      // Check blob size - error messages are usually small
      if (blob.size < 2000) {
        try {
          const text = await blob.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || 'File too small, likely an error message');
        } catch (e) {
          // If it's not JSON, it might be a valid small file, so continue
          if (!isPDF && !isImage) {
            throw new Error('File too small, likely an error message');
          }
        }
      }
      
      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const extension = isPDF ? 'pdf' : 'jpeg';
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${passId}.${extension}`;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
    } catch (error: any) {
      // If it's an axios error with a blob response, try to extract error message
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || `Server error: ${error.response.status}`);
        } catch (e) {
          throw new Error(`Server error: ${error.response.status}`);
        }
      }
      throw error;
    }
  },
};

